import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './About.css';
import Footer from "./Footer";

gsap.registerPlugin(ScrollTrigger);

const About = ({ lang }) => {
    const containerRef = useRef(null);
    const blurRef = useRef(null);
    const whiteDimRef = useRef(null);
    const scrollContentRef = useRef(null);
    const hasSnapped = useRef(false);

    // (파랄랙스 요소들을 위한 ref 관련 코드가 있으나, 아래 useEffect 내에서 직접 DOM 접근)

    const blurWhiteDimTween = () => {
        if (!containerRef.current) return;
        const scrollPosition = containerRef.current.scrollTop;
        const containerHeight = containerRef.current.scrollHeight;
        const ratio = scrollPosition / (containerHeight * 0.3);

        if (blurRef.current) {
            blurRef.current.style.webkitBackdropFilter = `blur(${Math.min(ratio * 60, 60)}px)`;
            blurRef.current.style.backdropFilter = `blur(${Math.min(ratio * 60, 60)}px)`;
        }
        if (whiteDimRef.current) {
            whiteDimRef.current.style.opacity = Math.min(ratio * 0.3, 0.3);
        }
    };

    useEffect(() => {
        if (!containerRef.current || !scrollContentRef.current) return;
        const containerEl = containerRef.current;
        const scrollContent = scrollContentRef.current;
        const blurEl = blurRef.current;
        const whiteDimEl = whiteDimRef.current;
        hasSnapped.current = false;

        // 배열에 생성된 ScrollTrigger 인스턴스를 저장하여 cleanup 시 kill 처리
        const imageParallaxTriggers = [];
        const textParallaxTriggers = [];

        // 이미지 파랄랙스 효과: id를 이용해 DOM 요소 가져오기
        const imageParallaxItems = [
            document.getElementById('parallax_01'),
            document.getElementById('parallax_03'),
            document.getElementById('parallax_05'),
        ];
        imageParallaxItems.forEach((item) => {
            if (!item) return;
            const moveDist = 80; // 최대 이동값
            const trigger = ScrollTrigger.create({
                trigger: item,
                scroller: containerEl,
                start: "top bottom",
                end: "top top",
                scrub: 2.5,
                onUpdate: (self) => {
                    const p = self.progress;
                    item.style.transform = `translateY(${-moveDist * p}px)`;
                },
            });
            imageParallaxTriggers.push(trigger);
        });

        // 텍스트 파랄랙스 효과
        const textParallaxItems = [
            document.getElementById('parallax_02'),
            document.getElementById('parallax_04'),
            document.getElementById('parallax_06'),
        ];
        textParallaxItems.forEach((item) => {
            if (!item) return;
            const distance = 150;
            gsap.set(item, { y: 0 });
            const trigger = ScrollTrigger.create({
                trigger: item,
                invalidateOnRefresh: true,
                scroller: containerEl,
                start: "top bottom",
                end: "top top",
                scrub: 2.6,
            });
            gsap.fromTo(
                item,
                { y: 0 },
                {
                    y: -distance,
                    immediateRender: false,
                    scrollTrigger: trigger,
                }
            );
            textParallaxTriggers.push(trigger);
        });

        if (blurEl) {
            blurEl.style.webkitBackdropFilter = "blur(0px)";
            blurEl.style.backdropFilter = "blur(0px)";
        }
        if (whiteDimEl) whiteDimEl.style.opacity = "0";

        const viewportHeight = window.innerHeight;
        let activeTween = null;

        const handleResize = () => {
            requestAnimationFrame(() => {
                blurWhiteDimTween();
            });
            ScrollTrigger.refresh();
        };

        let startY = 0;
        let moveY = 0;
        let scrollStart = 0;
        let isDragging = false;

        const handleOverScroll = (event) => {
            if (!containerEl) return;
            const scrollTop = containerEl.scrollTop;
            if (scrollTop > 0) return;

            if (event.type === "touchstart") {
                startY = event.touches[0].clientY;
            } else if (event.type === "touchmove") {
                moveY = event.touches[0].clientY;
                if (moveY > startY) {
                    window.dispatchEvent(new CustomEvent("closeSubToMain"));
                }
            } else if (event.type === "touchend") {
                if (moveY > startY) {
                    window.dispatchEvent(new CustomEvent("closeSubToMain"));
                }
            }
            else if (event.type === "wheel") {
                if (event.deltaY < 0) {
                    window.dispatchEvent(new CustomEvent("closeSubToMain"));
                }
            }
        };

        // 메인 스크롤 트리거 (snap 효과)
        const mainTrigger = ScrollTrigger.create({
            trigger: scrollContent,
            scroller: containerEl,
            start: "top top",
            end: "bottom bottom",
            scrub: 0.3,
            snap: {
                snapTo: (progress, self) => {
                    const scrollPos = self.scroll();
                    const targetSnapPos = viewportHeight - 100;
                    if (self.direction > 0 && progress > 0.1 && !hasSnapped.current && targetSnapPos > scrollPos) {
                        hasSnapped.current = true;
                        containerEl.style.pointerEvents = "none";
                        activeTween = gsap.to(containerEl, {
                            scrollTo: targetSnapPos,
                            duration: 0.4,
                            ease: "cubic.out",
                            onComplete: () => {
                                activeTween = null;
                                containerEl.style.pointerEvents = "auto";
                            },
                        });
                        return false;
                    }
                    return false;
                },
                duration: 0.01,
                delay: 0,
                ease: "power2.out",
            },
            onUpdate: (self) => {
                blurWhiteDimTween();
                const scrollPosition = self.scroll();
                const targetSnapPos = viewportHeight - 100;
                if (self.direction > 0 || scrollPosition < targetSnapPos) hasSnapped.current = false;
            },
        });

        window.addEventListener("resize", handleResize);
        containerEl.addEventListener("touchstart", handleOverScroll, { passive: true });
        containerEl.addEventListener("touchmove", handleOverScroll, { passive: true });
        containerEl.addEventListener('touchend', handleOverScroll, { passive: true });
        // window.addEventListener("wheel", handleOverScroll, { passive: false });

        return () => {
            window.removeEventListener("resize", handleResize);
            containerEl.removeEventListener("touchstart", handleOverScroll);
            containerEl.removeEventListener("touchmove", handleOverScroll);
            containerEl.removeEventListener('touchend', handleOverScroll);
            // window.removeEventListener("wheel", handleOverScroll);
            // 진행중인 gsap tween cleanup
            if (activeTween) {
                activeTween.kill();
            }
            // 메인 스크롤 트리거 cleanup
            mainTrigger.kill();
            // 생성된 모든 파랄랙스 스크롤 트리거 cleanup
            imageParallaxTriggers.forEach((trigger) => trigger.kill());
            textParallaxTriggers.forEach((trigger) => trigger.kill());
        };
    }, []); // 의존성 배열에 필요한 값이 있다면 추가

    useEffect(() => {
        // 마우스 무브 이벤트를 통한 이미지 관련 애니메이션 처리
        const commonImageParams = {
            translationFactorX: 10,
            translationFactorY: 10,
            translationDirectionMultiplier: 1.2,
            nonClosestTranslationMultiplier: 0.5,
            tiltFactorX: 8,
            tiltFactorY: 8,
            tiltDirectionMultiplier: -1,
            distanceMultiplier: 1,
            zoom: { maxDistance: 300, minScale: 1, maxScale: 1.1 },
            transitionDuration: 1,
            applyTiltToClosestOnly: false,
            applyZoomToClosestOnly: false,
        };

        const params = {
            about_img_01: { ...commonImageParams },
            about_img_02: { ...commonImageParams },
            about_img_03: { ...commonImageParams },
        };

        const handleMouseMove = (e) => {
            const image1 = document.querySelector('.about_img_01');
            const image2 = document.querySelector('.about_img_02');
            const image3 = document.querySelector('.about_img_03');

            const images = [image1, image2, image3].filter((img) => img);
            let closestImage = null;
            let closestDistance = Infinity;

            images.forEach((img) => {
                const rect = img.getBoundingClientRect();
                const imgCenterX = rect.left + rect.width / 2;
                const imgCenterY = rect.top + rect.height / 2;
                const dx = e.clientX - imgCenterX;
                const dy = e.clientY - imgCenterY;
                const distance = Math.sqrt(dx * dx + dy * dy);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestImage = img;
                }
            });

            const imagesData = [
                { element: image1, params: params.about_img_01 },
                { element: image2, params: params.about_img_02 },
                { element: image3, params: params.about_img_03 },
            ];

            imagesData.forEach(({ element, params }) => {
                if (!element) return;
                const {
                    translationFactorX,
                    translationFactorY,
                    translationDirectionMultiplier,
                    nonClosestTranslationMultiplier,
                    tiltFactorX,
                    tiltFactorY,
                    tiltDirectionMultiplier,
                    distanceMultiplier,
                    zoom,
                    transitionDuration,
                    applyTiltToClosestOnly,
                    applyZoomToClosestOnly,
                } = params;

                const rect = element.getBoundingClientRect();
                const imageCenterX = rect.left + rect.width / 2;
                const imageCenterY = rect.top + rect.height / 2;
                const dx = e.clientX - imageCenterX;
                const dy = e.clientY - imageCenterY;

                let normXImage = dx / (rect.width / 2);
                let normYImage = dy / (rect.height / 2);
                normXImage = Math.max(-1, Math.min(1, normXImage));
                normYImage = Math.max(-1, Math.min(1, normYImage));

                const currentDistance = Math.sqrt(dx * dx + dy * dy);
                const dynamicMaxDistance = rect.width * distanceMultiplier;
                const clampedDistance = Math.min(currentDistance, dynamicMaxDistance);

                const computedX =
                    translationDirectionMultiplier *
                    normXImage *
                    translationFactorX *
                    (element === closestImage ? 1 : nonClosestTranslationMultiplier);
                const computedY =
                    translationDirectionMultiplier *
                    normYImage *
                    translationFactorY *
                    (element === closestImage ? 1 : nonClosestTranslationMultiplier);

                const computedScale = (applyZoomToClosestOnly && element !== closestImage)
                    ? 1
                    : zoom.maxScale -
                    (clampedDistance / dynamicMaxDistance) * (zoom.maxScale - zoom.minScale);

                const computedRotationX = (applyTiltToClosestOnly && element !== closestImage)
                    ? 0
                    : tiltDirectionMultiplier * (-normYImage * tiltFactorX);
                const computedRotationY = (applyTiltToClosestOnly && element !== closestImage)
                    ? 0
                    : tiltDirectionMultiplier * (normXImage * tiltFactorY);

                gsap.to(element, {
                    duration: transitionDuration,
                    x: computedX,
                    y: computedY,
                    rotationX: computedRotationX,
                    rotationY: computedRotationY,
                    scale: computedScale,
                    ease: "power2.out",
                    overwrite: true,
                });
            });
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
        };
    }, []);

    return (
        <>
            <div id="about" ref={containerRef} className="about-container">
                <div ref={blurRef} className="about-background-blur"></div>
                <div ref={whiteDimRef} className="about-background-white"></div>

                <div ref={scrollContentRef} className="about-scroll">
                    <div className="section-1">
                        <div className="about-main">
                            <div className="about-main-child2">
                                {lang === 'ENG'
                                    ? <>Technologies for Implementing<br />Interaction Design and Visual Details</>
                                    : <>인터랙션 디자인과 <br />시각적 디테일을 구현하는 기술</>
                                }

                            </div>
                            <div className="about-main-child3">
                                {lang === 'ENG'
                                    ? <>HNINE provides effective and flexible visual and technological solutions<br />
                                        at the intersection of interaction design</>
                                    : <>에이치나인은 인터랙션 디자인의 교차점에서 유연하고 효과적인 시각 및 기술 솔루션을 제공합니다.</>
                                }

                            </div>
                        </div>
                    </div>
                    <div className="section-2">
                        <div className="about-sub1">
                            <div id="parallax_01" className="about-sub-img">
                                <div className="about_img_01">
                                    <img src={process.env.PUBLIC_URL + "/data/img/about/about_sub1_img.png"} alt="About Sub Img" />
                                </div>
                            </div>
                            <div id="parallax_02" className="about-sub-title-1">
                                {lang === 'ENG'
                                    ? <>Resolve gray areas</>
                                    : <>애매한 영역을<br />보다 명확하게</>
                                }
                                <div className="about-sub-body">
                                    {lang === 'ENG'
                                        ? <>Bridging planning, design, and development through<br />
                                            motion and prototype development,<br />
                                            we enhance output quality with quick execution and,<br />
                                            when necessary, take full responsibility for development</>
                                        : <>모션 및 프로토타입 개발을 통해<br />
                                            기획, 디자인, 개발을 연결하고,<br />
                                            빠른 실행력으로 결과물의 품질을 향상시키며,<br />
                                            필요 시 개발까지 전적으로 책임집니다.</>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="about-sub2">
                            <div id="parallax_03" className="about-sub-img">
                                <div className="about_img_02">
                                    <img src={process.env.PUBLIC_URL + "/data/img/about/about_sub2_img.png"} alt="About Sub Img" />
                                </div>
                            </div>
                            <div id="parallax_04" className="about-sub-title-2">
                                {lang === 'ENG'
                                    ? <>Make digital content alive</>
                                    : <>디지털 콘텐츠가 살아 숨 쉬도록</>
                                }

                                <div className="about-sub-body">
                                    {lang === 'ENG'
                                        ? <>We design interactions from<br />
                                            a UX or VI perspective and visualize them</>
                                        : <>UX, VI 관점에서 인터랙션을 설계하고<br />
                                            그 흐름을 시각적으로 구현합니다.
                                        </>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="about-sub3">
                            <div id="parallax_05" className="about-sub-img">
                                <div className="about_img_03">
                                    <img src={process.env.PUBLIC_URL + '/data/img/about/about_sub3_img.png'} alt="About Sub Img" />
                                </div>
                            </div>
                            <div id="parallax_06" className="about-sub-title-3">
                                {lang === 'ENG'
                                    ? <>Drive R&D and Integration</>
                                    : <>기술 연구도<br />적극적으로</>
                                }

                                <div className="about-sub-body">
                                    {lang === 'ENG'
                                        ? <>We proactively acquire the necessary technologies<br />
                                            from a visual or interaction perspective and<br />
                                            apply them to the project.</>
                                        : <>시각적 표현이나 인터랙션 관점에서<br />
                                            필요한 기술을 능동적으로 습득하고<br />
                                            프로젝트에 적용합니다.
                                        </>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="section-2-mobie">
                        <div className="about-sub1">
                            <div id="parallax_01" className="about-sub-img">
                                <img src={process.env.PUBLIC_URL + "/data/img/about/about_sub1_img.png"} alt="About Sub Img" />
                            </div>
                            <div id="parallax_02" className="about-sub-title-1">
                                {lang === 'ENG'
                                    ? <>Resolve gray areas</>
                                    : <>애매한 영역을<br />보다 명확하게</>
                                }
                                <div className="about-sub-body">
                                    {lang === 'ENG'
                                        ? <>Bridging planning, design, and development through<br />
                                            motion and prototype development,<br />
                                            we enhance output quality with quick execution and,<br />
                                            when necessary, take full responsibility for development</>
                                        : <>모션 및 프로토타입 개발을 통해<br />
                                            기획, 디자인, 개발을 연결하고,<br />
                                            빠른 실행력으로 결과물의 품질을 향상시키며,<br />
                                            필요 시 개발까지 전적으로 책임집니다.</>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="about-sub2">
                            <div id="parallax_03" className="about-sub-img">
                                <img src={process.env.PUBLIC_URL + "/data/img/about/about_sub2_img.png"} alt="About Sub Img" />
                            </div>
                            <div id="parallax_04" className="about-sub-title-2">
                                {lang === 'ENG'
                                    ? <>Make digital content alive</>
                                    : <>디지털 콘텐츠가 살아 숨 쉬도록</>
                                }

                                <div className="about-sub-body">
                                    {lang === 'ENG'
                                        ? <>We design interactions from<br />
                                            a UX or VI perspective and visualize them</>
                                        : <>UX, VI 관점에서 인터랙션을 설계하고<br />
                                            그 흐름을 시각적으로 구현합니다.
                                        </>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="about-sub3">
                            <div id="parallax_05" className="about-sub-img">
                                <img src={process.env.PUBLIC_URL + "/data/img/about/about_sub3_img.png"} alt="About Sub Img" />
                            </div>
                            <div id="parallax_06" className="about-sub-title-3">
                                {lang === 'ENG'
                                    ? <>Drive R&D and Integration</>
                                    : <>기술 연구도<br />적극적으로</>
                                }

                                <div className="about-sub-body">
                                    {lang === 'ENG'
                                        ? <>We proactively acquire the necessary technologies<br />
                                            from a visual or interaction perspective and<br />
                                            apply them to the project.</>
                                        : <>시각적 표현이나 인터랙션 관점에서<br />
                                            필요한 기술을 능동적으로 습득하고<br />
                                            프로젝트에 적용합니다.
                                        </>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="section-3">
                        <Footer
                            lang={lang}
                            isContactPage={false} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default About;